
import React, { useState, useRef } from 'react';
import { Helmet } from 'react-helmet-async';
import { useForm } from 'react-hook-form';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Separator } from '@/components/ui/separator';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Card, CardContent } from '@/components/ui/card';
import { Check, Copy, Eye, ImagePlus, Plus, Trash, X, Save, SendHorizontal, Mail, Loader2, Upload, Download, FileSpreadsheet, Users, Database } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { fetchProducts } from '@/services/productService';
import { useQuery, useMutation } from '@tanstack/react-query';
import { Product } from '@/types/product';
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { createNewsletter, sendNewsletter, getNewsletterSubscribers } from '@/services/newsletterService';
import { supabase } from '@/integrations/supabase/client';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import * as XLSX from 'xlsx';

interface NewsletterFormData {
  subject: string;
  preheader: string;
  heading: string;
  bodyText: string;
  buttonText: string;
  buttonUrl: string;
  featuredProducts: string[];
}

type BlockType = 'text' | 'image' | 'button' | 'spacer' | 'divider' | 'products';

interface NewsletterBlock {
  id: string;
  type: BlockType;
  content: any;
}

interface EmailTemplate {
  id: string;
  name: string;
  subject: string;
  preheader: string;
}

const templates: EmailTemplate[] = [
  { id: 'new-arrivals', name: 'New Arrivals', subject: 'Check out our latest products!', preheader: 'Exclusive new arrivals just for you' },
  { id: 'sale', name: 'Sale Announcement', subject: 'SALE! Up to 50% off', preheader: 'Limited time offer - Don\'t miss out!' },
  { id: 'holiday', name: 'Holiday Special', subject: 'Holiday Specials Just For You', preheader: 'Celebrate the season with these exclusive offers' },
  { id: 'welcome', name: 'Welcome Series', subject: 'Welcome to Meubles Karim', preheader: 'Thanks for joining our family' },
];

const NewsletterBuilder: React.FC = () => {
  const [activeTab, setActiveTab] = useState<string>('design');
  const [selectedTemplate, setSelectedTemplate] = useState<EmailTemplate | null>(null);
  const [blocks, setBlocks] = useState<NewsletterBlock[]>([
    { id: '1', type: 'text', content: { text: 'Welcome to our newsletter!' } },
    { id: '2', type: 'image', content: { src: 'https://images.unsplash.com/photo-1586023492125-27b2c045efd7?q=80&w=800&h=400&fit=crop', alt: 'Newsletter header' } },
    { id: '3', type: 'text', content: { text: 'Check out our latest products and offers.' } },
  ]);
  const [previewMode, setPreviewMode] = useState<boolean>(false);
  const [savedNewsletterId, setSavedNewsletterId] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [isSendingTest, setIsSendingTest] = useState(false);
  const [showSendDialog, setShowSendDialog] = useState(false);
  
  // Excel upload state
  const [recipientSource, setRecipientSource] = useState<'database' | 'excel'>('database');
  const [excelEmails, setExcelEmails] = useState<{ email: string; firstName?: string }[]>([]);
  const [excelFileName, setExcelFileName] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  
  const { toast } = useToast();
  
  const { data: products = [] } = useQuery({
    queryKey: ['products-for-newsletter'],
    queryFn: () => fetchProducts({ featured: true }),
  });

  const { data: subscriberCount = 0 } = useQuery({
    queryKey: ['newsletter-subscriber-count'],
    queryFn: async () => {
      const subscribers = await getNewsletterSubscribers();
      return subscribers.length;
    },
  });
  
  const { register, handleSubmit, formState: { errors }, setValue, watch } = useForm<NewsletterFormData>({
    defaultValues: {
      subject: 'Our Latest Newsletter',
      preheader: 'Check out our latest news and offers',
      heading: 'Welcome to Meubles Karim',
      bodyText: 'We\'re excited to share our latest collection with you. Discover beautiful furniture pieces for your home.',
      buttonText: 'Shop Now',
      buttonUrl: '/products',
      featuredProducts: [],
    }
  });

  const addBlock = (type: BlockType) => {
    const newBlock: NewsletterBlock = {
      id: Date.now().toString(),
      type,
      content: getDefaultContentForBlockType(type)
    };
    
    setBlocks([...blocks, newBlock]);
  };
  
  const getDefaultContentForBlockType = (type: BlockType) => {
    switch(type) {
      case 'text':
        return { text: 'New text block' };
      case 'image':
        return { src: 'https://via.placeholder.com/600x300', alt: 'Image description' };
      case 'button':
        return { text: 'Click Me', url: '/products' };
      case 'spacer':
        return { height: 20 };
      case 'divider':
        return { color: '#EEEEEE' };
      case 'products':
        return { ids: [] };
      default:
        return {};
    }
  };
  
  const removeBlock = (id: string) => {
    setBlocks(blocks.filter(block => block.id !== id));
  };
  
  const updateBlockContent = (id: string, content: any) => {
    setBlocks(blocks.map(block => 
      block.id === id ? { ...block, content: { ...block.content, ...content } } : block
    ));
  };
  
  const moveBlockUp = (index: number) => {
    if (index === 0) return;
    const newBlocks = [...blocks];
    [newBlocks[index - 1], newBlocks[index]] = [newBlocks[index], newBlocks[index - 1]];
    setBlocks(newBlocks);
  };
  
  const moveBlockDown = (index: number) => {
    if (index === blocks.length - 1) return;
    const newBlocks = [...blocks];
    [newBlocks[index], newBlocks[index + 1]] = [newBlocks[index + 1], newBlocks[index]];
    setBlocks(newBlocks);
  };
  
  // Generate HTML content from blocks for email
  const generateEmailHTML = (subject: string, preheader: string): string => {
    let blocksHTML = '';
    
    blocks.forEach(block => {
      switch(block.type) {
        case 'text':
          blocksHTML += `<div style="margin-bottom: 16px; text-align: center; font-size: 16px; line-height: 1.6;">${block.content.text}</div>`;
          break;
        case 'image':
          blocksHTML += `<div style="margin-bottom: 16px; text-align: center;"><img src="${block.content.src}" alt="${block.content.alt}" style="max-width: 100%; height: auto; border-radius: 8px;" /></div>`;
          break;
        case 'button':
          blocksHTML += `<div style="margin-bottom: 16px; text-align: center;"><a href="${block.content.url}" style="display: inline-block; background-color: #9F8E7D; color: white; padding: 12px 24px; text-decoration: none; border-radius: 4px; font-weight: 500;">${block.content.text}</a></div>`;
          break;
        case 'spacer':
          blocksHTML += `<div style="height: ${block.content.height}px;"></div>`;
          break;
        case 'divider':
          blocksHTML += `<hr style="border: 0; border-top: 1px solid ${block.content.color}; margin: 16px 0;" />`;
          break;
        case 'products':
          const selectedProducts = products.filter((p: Product) => block.content.ids.includes(p.id)).slice(0, 4);
          if (selectedProducts.length > 0) {
            blocksHTML += `<div style="margin-bottom: 16px;"><table width="100%" cellpadding="8" cellspacing="0"><tr>`;
            selectedProducts.forEach((product: Product, idx: number) => {
              blocksHTML += `<td width="50%" style="vertical-align: top; border: 1px solid #eee; border-radius: 8px; padding: 12px;">`;
              if (product.images?.[0]) {
                blocksHTML += `<img src="${product.images[0]}" alt="${product.name}" style="width: 100%; height: 120px; object-fit: cover; border-radius: 4px; margin-bottom: 8px;" />`;
              }
              blocksHTML += `<p style="font-weight: 600; margin: 0 0 4px 0; font-size: 14px;">${product.name}</p>`;
              blocksHTML += `<p style="margin: 0; color: #9F8E7D; font-weight: 500;">$${product.price.toFixed(2)}</p>`;
              blocksHTML += `</td>`;
              if (idx === 1) blocksHTML += `</tr><tr>`;
            });
            blocksHTML += `</tr></table></div>`;
          }
          break;
      }
    });

    return `
<!DOCTYPE html>
<html>
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>${subject}</title>
</head>
<body style="font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif; line-height: 1.6; color: #333; max-width: 600px; margin: 0 auto; padding: 0; background-color: #f5f5f5;">
  <div style="background-color: #ffffff; margin: 20px; border-radius: 8px; overflow: hidden; box-shadow: 0 2px 8px rgba(0,0,0,0.1);">
    <div style="background-color: #9F8E7D; padding: 24px; text-align: center;">
      <h1 style="color: white; margin: 0; font-size: 24px; font-weight: 600;">${subject}</h1>
      ${preheader ? `<p style="color: rgba(255,255,255,0.9); margin: 8px 0 0 0; font-size: 14px;">${preheader}</p>` : ''}
    </div>
    <div style="padding: 24px;">
      ${blocksHTML}
    </div>
    <div style="background-color: #f9f9f9; padding: 20px; text-align: center; border-top: 1px solid #eee;">
      <p style="margin: 0 0 8px 0; font-size: 14px; color: #666;">Meubles Karim | Route Hammamet Nord vers Nabeul, Hammamet, Tunisia</p>
      <p style="margin: 0; font-size: 12px; color: #999;">
        <a href="#" style="color: #9F8E7D; text-decoration: none;">Unsubscribe</a> • 
        <a href="#" style="color: #9F8E7D; text-decoration: none;">View in browser</a>
      </p>
    </div>
  </div>
</body>
</html>`;
  };

  const onSubmit = async (data: NewsletterFormData) => {
    setIsSaving(true);
    try {
      const htmlContent = generateEmailHTML(data.subject, data.preheader);
      
      const newsletter = await createNewsletter({
        subject: data.subject,
        preheader: data.preheader,
        content: {
          html: htmlContent,
          blocks: blocks,
        },
      });
      
      if (newsletter?.id) {
        setSavedNewsletterId(newsletter.id);
        toast({
          title: 'Newsletter saved!',
          description: 'Your newsletter has been saved successfully. You can now send it to subscribers.',
        });
      }
    } catch (error) {
      console.error('Error saving newsletter:', error);
      toast({
        title: 'Error',
        description: 'Failed to save newsletter. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setIsSaving(false);
    }
  };
  
  const sendTestEmail = async () => {
    setIsSendingTest(true);
    try {
      const formData = watch();
      const htmlContent = generateEmailHTML(formData.subject, formData.preheader);
      
      // Get current user's email for test
      const { data: { user } } = await supabase.auth.getUser();
      if (!user?.email) {
        toast({
          title: 'Error',
          description: 'Could not find your email address. Please log in again.',
          variant: 'destructive',
        });
        return;
      }

      // Use Supabase Edge Function to send test email
      const { data, error } = await supabase.functions.invoke('send-test-email', {
        body: {
          to: user.email,
          subject: `[TEST] ${formData.subject}`,
          html: htmlContent,
        },
      });

      if (error) throw error;

      toast({
        title: 'Test email sent!',
        description: `A test email has been sent to ${user.email}`,
      });
    } catch (error) {
      console.error('Error sending test email:', error);
      toast({
        title: 'Error',
        description: 'Failed to send test email. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setIsSendingTest(false);
    }
  };

  const handleSendToAllSubscribers = async () => {
    if (!savedNewsletterId) {
      toast({
        title: 'Save first',
        description: 'Please save your newsletter before sending.',
        variant: 'destructive',
      });
      return;
    }

    setIsSending(true);
    try {
      await sendNewsletter(savedNewsletterId);
      setShowSendDialog(false);
      toast({
        title: 'Newsletter sent!',
        description: `Your newsletter has been sent to ${subscriberCount} subscribers.`,
      });
    } catch (error) {
      console.error('Error sending newsletter:', error);
      toast({
        title: 'Error',
        description: 'Failed to send newsletter. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setIsSending(false);
    }
  };

  // Excel file handling functions
  const downloadExcelTemplate = () => {
    // Create template data
    const templateData = [
      { email: 'john@example.com', first_name: 'John' },
      { email: 'jane@example.com', first_name: 'Jane' },
      { email: 'bob@example.com', first_name: 'Bob' },
    ];

    // Create worksheet
    const ws = XLSX.utils.json_to_sheet(templateData);
    
    // Set column widths
    ws['!cols'] = [
      { wch: 30 }, // email column
      { wch: 20 }, // first_name column
    ];

    // Create workbook
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Recipients');

    // Download file
    XLSX.writeFile(wb, 'newsletter_recipients_template.xlsx');
    
    toast({
      title: 'Template downloaded!',
      description: 'Fill in the template with your recipients and upload it.',
    });
  };

  const handleExcelUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const data = new Uint8Array(e.target?.result as ArrayBuffer);
        const workbook = XLSX.read(data, { type: 'array' });
        
        // Get the first sheet
        const sheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[sheetName];
        
        // Convert to JSON
        const jsonData = XLSX.utils.sheet_to_json<{ email: string; first_name?: string }>(worksheet);
        
        // Validate and filter emails
        const validEmails = jsonData
          .filter(row => row.email && typeof row.email === 'string' && row.email.includes('@'))
          .map(row => ({
            email: row.email.trim().toLowerCase(),
            firstName: row.first_name?.trim() || undefined,
          }));

        if (validEmails.length === 0) {
          toast({
            title: 'No valid emails found',
            description: 'Please ensure your Excel file has an "email" column with valid email addresses.',
            variant: 'destructive',
          });
          return;
        }

        // Remove duplicates
        const uniqueEmails = validEmails.filter(
          (item, index, self) => index === self.findIndex((t) => t.email === item.email)
        );

        setExcelEmails(uniqueEmails);
        setExcelFileName(file.name);
        setRecipientSource('excel');
        
        toast({
          title: 'Excel file loaded!',
          description: `Found ${uniqueEmails.length} valid email addresses.`,
        });
      } catch (error) {
        console.error('Error parsing Excel file:', error);
        toast({
          title: 'Error reading file',
          description: 'Please ensure you uploaded a valid Excel file (.xlsx or .xls).',
          variant: 'destructive',
        });
      }
    };
    reader.readAsArrayBuffer(file);
    
    // Reset input so same file can be uploaded again
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const clearExcelEmails = () => {
    setExcelEmails([]);
    setExcelFileName(null);
    setRecipientSource('database');
  };

  const handleSendToExcelRecipients = async () => {
    if (excelEmails.length === 0) {
      toast({
        title: 'No recipients',
        description: 'Please upload an Excel file with email addresses first.',
        variant: 'destructive',
      });
      return;
    }

    setIsSending(true);
    try {
      const formData = watch();
      const htmlContent = generateEmailHTML(formData.subject, formData.preheader);

      // Send emails via Supabase Edge Function
      const { data, error } = await supabase.functions.invoke('send-bulk-email', {
        body: {
          recipients: excelEmails,
          subject: formData.subject,
          html: htmlContent,
        },
      });

      if (error) throw error;

      setShowSendDialog(false);
      toast({
        title: 'Newsletter sent!',
        description: `Your newsletter has been sent to ${excelEmails.length} recipients from the Excel file.`,
      });
    } catch (error) {
      console.error('Error sending newsletter to Excel recipients:', error);
      toast({
        title: 'Error',
        description: 'Failed to send newsletter. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setIsSending(false);
    }
  };

  // Combined send handler
  const handleSendNewsletter = async () => {
    if (recipientSource === 'database') {
      await handleSendToAllSubscribers();
    } else {
      await handleSendToExcelRecipients();
    }
  };

  const getRecipientCount = () => {
    return recipientSource === 'database' ? subscriberCount : excelEmails.length;
  };
  
  const applyTemplate = (template: EmailTemplate) => {
    setValue('subject', template.subject);
    setValue('preheader', template.preheader);
    setSelectedTemplate(template);
    
    // Add template-specific blocks
    if (template.id === 'sale') {
      setBlocks([
        { id: '1', type: 'text', content: { text: 'SALE! Up to 50% off' } },
        { id: '2', type: 'image', content: { src: 'https://images.unsplash.com/photo-1589492477829-5e65395b66cc?q=80&w=800&h=400&fit=crop', alt: 'Sale banner' } },
        { id: '3', type: 'text', content: { text: 'Hurry! Limited time offer on our most popular items.' } },
        { id: '4', type: 'button', content: { text: 'Shop the Sale', url: '/products' } },
      ]);
    } else if (template.id === 'new-arrivals') {
      setBlocks([
        { id: '1', type: 'text', content: { text: 'New Arrivals Just In!' } },
        { id: '2', type: 'image', content: { src: 'https://images.unsplash.com/photo-1586023492125-27b2c045efd7?q=80&w=800&h=400&fit=crop', alt: 'New arrivals' } },
        { id: '3', type: 'text', content: { text: 'Be the first to shop our latest collection.' } },
        { id: '4', type: 'products', content: { ids: ['1', '2', '3'] } },
        { id: '5', type: 'button', content: { text: 'View All New Arrivals', url: '/products' } },
      ]);
    }
    
    toast({
      title: 'Template applied',
      description: `The "${template.name}" template has been applied.`,
    });
  };
  
  const renderBlockEditor = (block: NewsletterBlock, index: number) => {
    const renderContentEditor = () => {
      switch(block.type) {
        case 'text':
          return (
            <Textarea
              value={block.content.text}
              onChange={(e) => updateBlockContent(block.id, { text: e.target.value })}
              className="min-h-[100px]"
            />
          );
        case 'image':
          return (
            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <Input
                  value={block.content.src}
                  onChange={(e) => updateBlockContent(block.id, { src: e.target.value })}
                  placeholder="Image URL"
                />
                <Button variant="outline" size="icon">
                  <ImagePlus size={18} />
                </Button>
              </div>
              <Input
                value={block.content.alt}
                onChange={(e) => updateBlockContent(block.id, { alt: e.target.value })}
                placeholder="Alt text"
              />
              {block.content.src && (
                <img 
                  src={block.content.src} 
                  alt={block.content.alt} 
                  className="max-h-40 object-contain mx-auto border rounded"
                />
              )}
            </div>
          );
        case 'button':
          return (
            <div className="space-y-3">
              <Input
                value={block.content.text}
                onChange={(e) => updateBlockContent(block.id, { text: e.target.value })}
                placeholder="Button text"
              />
              <Input
                value={block.content.url}
                onChange={(e) => updateBlockContent(block.id, { url: e.target.value })}
                placeholder="Button URL"
              />
              <div className="flex justify-center">
                <Button
                  variant="outline"
                  className="bg-furniture-taupe text-white hover:bg-furniture-brown"
                  disabled
                >
                  {block.content.text || 'Button'}
                </Button>
              </div>
            </div>
          );
        case 'spacer':
          return (
            <div className="space-y-3">
              <Label>Height (px)</Label>
              <Input
                type="number"
                value={block.content.height}
                onChange={(e) => updateBlockContent(block.id, { height: parseInt(e.target.value) })}
                min={5}
                max={100}
              />
            </div>
          );
        case 'divider':
          return (
            <div className="space-y-3">
              <Label>Color</Label>
              <Input
                type="color"
                value={block.content.color}
                onChange={(e) => updateBlockContent(block.id, { color: e.target.value })}
                className="h-10 w-full"
              />
            </div>
          );
        case 'products':
          return (
            <div className="space-y-3">
              <Label>Select Products (up to 4)</Label>
              <Select
                value=""
                onValueChange={(value) => {
                  if (block.content.ids.length < 4) {
                    updateBlockContent(block.id, { 
                      ids: [...block.content.ids, value] 
                    });
                  }
                }}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Add products" />
                </SelectTrigger>
                <SelectContent>
                  {products.map((product: Product) => (
                    <SelectItem 
                      key={product.id} 
                      value={product.id}
                      disabled={block.content.ids.includes(product.id)}
                    >
                      {product.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              
              <div className="grid grid-cols-2 gap-2 mt-2">
                {block.content.ids.map((id: string) => {
                  const product = products.find((p: Product) => p.id === id);
                  if (!product) return null;
                  
                  return (
                    <div key={id} className="flex items-center justify-between bg-muted p-2 rounded">
                      <span className="text-sm truncate">{product.name}</span>
                      <Button 
                        variant="ghost" 
                        size="icon" 
                        className="h-6 w-6"
                        onClick={() => updateBlockContent(block.id, { 
                          ids: block.content.ids.filter((i: string) => i !== id) 
                        })}
                      >
                        <X size={14} />
                      </Button>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        default:
          return null;
      }
    };
    
    return (
      <Card key={block.id} className="mb-4 border-l-4" style={{ borderLeftColor: '#9F8E7D' }}>
        <CardContent className="p-4">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <span className="font-medium capitalize">{block.type} Block</span>
            </div>
            <div className="flex items-center gap-1">
              <Button 
                variant="ghost" 
                size="icon" 
                className="h-8 w-8"
                disabled={index === 0}
                onClick={() => moveBlockUp(index)}
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="m18 15-6-6-6 6"/>
                </svg>
              </Button>
              <Button 
                variant="ghost" 
                size="icon" 
                className="h-8 w-8"
                disabled={index === blocks.length - 1}
                onClick={() => moveBlockDown(index)}
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="m6 9 6 6 6-6"/>
                </svg>
              </Button>
              <Button 
                variant="ghost" 
                size="icon"
                className="h-8 w-8 text-red-500 hover:text-red-600 hover:bg-red-50"
                onClick={() => removeBlock(block.id)}
              >
                <Trash size={16} />
              </Button>
            </div>
          </div>
          
          <div className="mt-2">
            {renderContentEditor()}
          </div>
        </CardContent>
      </Card>
    );
  };
  
  const renderBlockPreview = (block: NewsletterBlock, index: number) => {
    switch(block.type) {
      case 'text':
        return (
          <div key={block.id} className="mb-4 px-2 text-center">
            {block.content.text}
          </div>
        );
      case 'image':
        return (
          <div key={block.id} className="mb-4 text-center">
            <img 
              src={block.content.src} 
              alt={block.content.alt} 
              className="max-w-full inline-block"
            />
          </div>
        );
      case 'button':
        return (
          <div key={block.id} className="mb-4 text-center">
            <a 
              href={block.content.url} 
              className="inline-block bg-furniture-taupe hover:bg-furniture-brown text-white px-6 py-2 rounded text-center no-underline"
              target="_blank"
              rel="noopener noreferrer"
            >
              {block.content.text}
            </a>
          </div>
        );
      case 'spacer':
        return (
          <div key={block.id} style={{ height: `${block.content.height}px` }}></div>
        );
      case 'divider':
        return (
          <div key={block.id} className="my-4">
            <hr style={{ borderTop: `1px solid ${block.content.color}` }} />
          </div>
        );
      case 'products':
        const selectedProducts = products.filter((p: Product) => 
          block.content.ids.includes(p.id)
        ).slice(0, 4);
        
        return (
          <div key={block.id} className="mb-4">
            <div className="grid grid-cols-2 gap-2">
              {selectedProducts.map((product: Product) => (
                <div key={product.id} className="border rounded p-2">
                  {product.images?.[0] && (
                    <img 
                      src={product.images[0]} 
                      alt={product.name} 
                      className="w-full h-32 object-cover mb-2 rounded"
                    />
                  )}
                  <p className="font-medium text-sm">{product.name}</p>
                  <p className="text-sm">${product.price.toFixed(2)}</p>
                </div>
              ))}
            </div>
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <div className="space-y-6">
      <Helmet>
        <title>Newsletter Builder | Admin Dashboard</title>
      </Helmet>
      
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-sans font-semibold">Newsletter Builder</h1>
        
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={sendTestEmail}
            disabled={isSendingTest}
          >
            {isSendingTest ? (
              <Loader2 size={16} className="mr-2 animate-spin" />
            ) : (
              <SendHorizontal size={16} className="mr-2" />
            )}
            Send Test
          </Button>
          <Button
            onClick={() => setPreviewMode(!previewMode)}
            variant="outline"
            size="sm"
          >
            {previewMode ? (
              <>
                <X size={16} className="mr-2" />
                Close Preview
              </>
            ) : (
              <>
                <Eye size={16} className="mr-2" />
                Preview
              </>
            )}
          </Button>
          <Button 
            onClick={handleSubmit(onSubmit)}
            size="sm"
            variant="outline"
            disabled={isSaving}
          >
            {isSaving ? (
              <Loader2 size={16} className="mr-2 animate-spin" />
            ) : (
              <Save size={16} className="mr-2" />
            )}
            Save Newsletter
          </Button>
          <Button 
            onClick={() => setShowSendDialog(true)}
            size="sm"
            disabled={!savedNewsletterId || isSending || (recipientSource === 'excel' && excelEmails.length === 0)}
          >
            {isSending ? (
              <Loader2 size={16} className="mr-2 animate-spin" />
            ) : (
              <Mail size={16} className="mr-2" />
            )}
            Send to {recipientSource === 'excel' ? 'Excel List' : 'All'} ({getRecipientCount()})
          </Button>
        </div>
      </div>
      
      {!previewMode ? (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left Column - Template Selection */}
          <div>
            <Card>
              <CardContent className="p-6">
                <h3 className="font-medium text-lg mb-4">Newsletter Templates</h3>
                <p className="text-sm text-muted-foreground mb-4">
                  Choose a template to get started quickly. The template will replace your current design.
                </p>
                
                <div className="space-y-3">
                  {templates.map(template => (
                    <Button
                      key={template.id}
                      variant={selectedTemplate?.id === template.id ? "default" : "outline"}
                      className="w-full justify-start"
                      onClick={() => applyTemplate(template)}
                    >
                      {selectedTemplate?.id === template.id && (
                        <Check size={16} className="mr-2" />
                      )}
                      {template.name}
                    </Button>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Recipients Selection Card */}
            <Card className="mt-6">
              <CardContent className="p-6">
                <h3 className="font-medium text-lg mb-4 flex items-center gap-2">
                  <Users size={20} />
                  Recipients
                </h3>
                
                <RadioGroup 
                  value={recipientSource} 
                  onValueChange={(value) => setRecipientSource(value as 'database' | 'excel')}
                  className="space-y-3"
                >
                  <div className="flex items-center space-x-3 p-3 rounded-lg border hover:bg-muted/50 cursor-pointer">
                    <RadioGroupItem value="database" id="database" />
                    <Label htmlFor="database" className="flex-1 cursor-pointer">
                      <div className="flex items-center gap-2">
                        <Database size={16} />
                        <span className="font-medium">Database Subscribers</span>
                      </div>
                      <p className="text-sm text-muted-foreground mt-1">
                        Send to {subscriberCount} subscribers in database
                      </p>
                    </Label>
                  </div>
                  
                  <div className="flex items-center space-x-3 p-3 rounded-lg border hover:bg-muted/50 cursor-pointer">
                    <RadioGroupItem value="excel" id="excel" />
                    <Label htmlFor="excel" className="flex-1 cursor-pointer">
                      <div className="flex items-center gap-2">
                        <FileSpreadsheet size={16} />
                        <span className="font-medium">Excel File</span>
                      </div>
                      <p className="text-sm text-muted-foreground mt-1">
                        Upload a list of recipients
                      </p>
                    </Label>
                  </div>
                </RadioGroup>

                {recipientSource === 'excel' && (
                  <div className="mt-4 space-y-3">
                    <Separator />
                    
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={downloadExcelTemplate}
                        className="flex-1"
                      >
                        <Download size={14} className="mr-2" />
                        Download Template
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => fileInputRef.current?.click()}
                        className="flex-1"
                      >
                        <Upload size={14} className="mr-2" />
                        Upload Excel
                      </Button>
                      <input
                        ref={fileInputRef}
                        type="file"
                        accept=".xlsx,.xls"
                        onChange={handleExcelUpload}
                        className="hidden"
                      />
                    </div>

                    {excelFileName && (
                      <div className="bg-muted rounded-lg p-3">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <FileSpreadsheet size={16} className="text-green-600" />
                            <span className="text-sm font-medium">{excelFileName}</span>
                          </div>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-6 w-6"
                            onClick={clearExcelEmails}
                          >
                            <X size={14} />
                          </Button>
                        </div>
                        <p className="text-sm text-muted-foreground mt-1">
                          {excelEmails.length} valid email{excelEmails.length !== 1 ? 's' : ''} loaded
                        </p>
                        
                        {excelEmails.length > 0 && (
                          <div className="mt-2 max-h-32 overflow-y-auto">
                            <div className="text-xs text-muted-foreground space-y-1">
                              {excelEmails.slice(0, 5).map((item, idx) => (
                                <div key={idx} className="truncate">
                                  {item.firstName ? `${item.firstName} - ` : ''}{item.email}
                                </div>
                              ))}
                              {excelEmails.length > 5 && (
                                <div className="text-muted-foreground/70">
                                  ... and {excelEmails.length - 5} more
                                </div>
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                    )}

                    {!excelFileName && (
                      <p className="text-xs text-muted-foreground">
                        Excel file should have columns: <strong>email</strong> (required) and <strong>first_name</strong> (optional)
                      </p>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
            
            <Card className="mt-6">
              <CardContent className="p-6">
                <h3 className="font-medium text-lg mb-4">Add Content Blocks</h3>
                <div className="grid grid-cols-2 gap-2">
                  <Button 
                    variant="outline" 
                    className="justify-start"
                    onClick={() => addBlock('text')}
                  >
                    <span className="mr-2">Aa</span>
                    Text
                  </Button>
                  <Button 
                    variant="outline"
                    className="justify-start"
                    onClick={() => addBlock('image')}
                  >
                    <ImagePlus size={16} className="mr-2" />
                    Image
                  </Button>
                  <Button 
                    variant="outline"
                    className="justify-start"
                    onClick={() => addBlock('button')}
                  >
                    <span className="mr-2 border rounded px-1 text-xs">BTN</span>
                    Button
                  </Button>
                  <Button 
                    variant="outline"
                    className="justify-start"
                    onClick={() => addBlock('spacer')}
                  >
                    <span className="mr-2">⋮</span>
                    Spacer
                  </Button>
                  <Button 
                    variant="outline"
                    className="justify-start"
                    onClick={() => addBlock('divider')}
                  >
                    <span className="mr-2">—</span>
                    Divider
                  </Button>
                  <Button 
                    variant="outline"
                    className="justify-start"
                    onClick={() => addBlock('products')}
                  >
                    <span className="mr-2">⊞</span>
                    Products
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
          
          {/* Middle Column - Newsletter Editor */}
          <div className="lg:col-span-2">
            <Card>
              <CardContent className="p-6">
                <h3 className="font-medium text-lg mb-4">Newsletter Settings</h3>
                
                <form className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="subject">Email Subject</Label>
                      <Input 
                        id="subject"
                        {...register('subject', { required: 'Subject is required' })}
                      />
                      {errors.subject && (
                        <p className="text-red-500 text-sm">{errors.subject.message}</p>
                      )}
                    </div>
                    
                    <div className="space-y-2">
                      <Label htmlFor="preheader">Preheader Text</Label>
                      <Input 
                        id="preheader"
                        {...register('preheader')}
                        placeholder="Brief summary shown in email clients"
                      />
                    </div>
                  </div>
                </form>
                
                <Separator className="my-6" />
                
                <h3 className="font-medium text-lg mb-4">Email Content</h3>
                
                {blocks.map((block, index) => renderBlockEditor(block, index))}
                
                {blocks.length === 0 && (
                  <div className="text-center py-8 bg-muted rounded-lg">
                    <p className="text-muted-foreground mb-4">Add a block to start building your newsletter</p>
                    <Button variant="outline" onClick={() => addBlock('text')}>
                      <Plus size={16} className="mr-2" />
                      Add First Block
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      ) : (
        <div className="flex justify-center">
          <div className="w-full max-w-xl bg-white rounded-lg shadow-lg overflow-hidden">
            {/* Email Preview Header */}
            <div className="bg-gray-100 p-4 border-b">
              <div className="flex items-center">
                <div className="w-3 h-3 rounded-full bg-red-500 mr-2"></div>
                <div className="w-3 h-3 rounded-full bg-yellow-500 mr-2"></div>
                <div className="w-3 h-3 rounded-full bg-green-500"></div>
                <div className="ml-4 text-sm text-gray-500">Email Preview</div>
              </div>
            </div>
            
            {/* Email Preview Content */}
            <div className="p-6">
              {/* Email Header */}
              <div className="text-center mb-6">
                <img 
                  src="/logo.svg" 
                  alt="Meubles Karim" 
                  className="h-12 mx-auto mb-4" 
                />
              </div>
              
              {/* Email Body */}
              <div className="space-y-4">
                {blocks.map((block, index) => renderBlockPreview(block, index))}
              </div>
              
              {/* Email Footer */}
              <div className="mt-8 pt-4 border-t text-center text-sm text-gray-500">
                <p>© {new Date().getFullYear()} Meubles Karim. All rights reserved.</p>
                <p className="mt-2">
                  <a href="#" className="text-furniture-taupe hover:underline">Unsubscribe</a> • 
                  <a href="#" className="text-furniture-taupe hover:underline ml-1">View in browser</a> • 
                  <a href="#" className="text-furniture-taupe hover:underline ml-1">Privacy Policy</a>
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Send Confirmation Dialog */}
      <AlertDialog open={showSendDialog} onOpenChange={setShowSendDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              Send Newsletter to {recipientSource === 'excel' ? 'Excel Recipients' : 'All Subscribers'}?
            </AlertDialogTitle>
            <AlertDialogDescription>
              {recipientSource === 'database' ? (
                <>
                  This will send your newsletter to <strong>{subscriberCount} subscribers</strong> from your database using Resend via Supabase Edge Functions.
                </>
              ) : (
                <>
                  This will send your newsletter to <strong>{excelEmails.length} recipients</strong> from your uploaded Excel file ({excelFileName}) using Resend via Supabase Edge Functions.
                </>
              )}
              <br />
              <span className="text-orange-600">This action cannot be undone.</span>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isSending}>Cancel</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleSendNewsletter}
              disabled={isSending}
              className="bg-furniture-taupe hover:bg-furniture-brown"
            >
              {isSending ? (
                <>
                  <Loader2 size={16} className="mr-2 animate-spin" />
                  Sending...
                </>
              ) : (
                <>
                  <Mail size={16} className="mr-2" />
                  Send to {getRecipientCount()} {recipientSource === 'excel' ? 'Recipients' : 'Subscribers'}
                </>
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default NewsletterBuilder;
